import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleInfo,
    faChevronDown,
    faChevronRight
} from '@fortawesome/pro-regular-svg-icons';
import classNames from "@/utils/class-names.ts";
import Tooltip from "@/components/tooltip";

export interface CardProps {
    id?: string;
    additionalClasses?: string;
    additionalHeaderClasses?: string;
    additionalContentClasses?: string;
    title?: string | React.ReactNode;
    info?: string;
    children: React.ReactNode;
    collapsable?: boolean;
    defaultCollapsed?: boolean;
    colors?: string;
    transparent?: boolean;
    showBorderAfterTitle?: boolean;
}

export const Card: React.FC<CardProps> = ({
    id,
    additionalClasses,
    additionalHeaderClasses,
    additionalContentClasses,
    title,
    info,
    children,
    colors,
    transparent,
    collapsable = false,
    defaultCollapsed = false,
    showBorderAfterTitle = true
}) => {

    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [contentHeight, setContentHeight] = useState<number>(0);
    const [hasMounted, setHasMounted] = useState(false);

    const contentRef = useRef<HTMLDivElement | null>(null);

    const reactId = crypto.randomUUID();
    const tooltipId = useMemo(() => `card-info-tooltip-${reactId}`, [reactId]);

    // Indique que le composant est monté afin d'activer les transitions uniquement
    // après le premier rendu et éviter une animation d'ouverture au chargement initial.
    useEffect(() => {
        setHasMounted(true);
    }, []);

    // Mesure la hauteur réelle du contenu pour animer l'ouverture/fermeture avec une
    // hauteur dynamique. Recalcule aussi la hauteur si le contenu change.
    useLayoutEffect(() => {
        if (!collapsable) {
            return () => {/* */}
        }

        const updateHeight = () => {
            if (contentRef.current) {
                setContentHeight(contentRef.current.scrollHeight);
            }
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);

        if (contentRef.current) {
            resizeObserver.observe(contentRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [children, collapsable]);
    
    const cardBackgroundColors = useMemo(() => {
        if(colors)
            return colors;
        
        if (transparent) {
            return 'bg-white/90 backdrop-blur-sm';
        }
        return 'bg-white border-slate-300';
    }, [transparent, colors])

    return (
        <div id={id}
            className={classNames(
                'rounded-lg shadow-sm pointer-events-auto border flex flex-col',
                additionalClasses ?? '',
                cardBackgroundColors
            )}>
            {title &&
                <div
                    className={classNames(
                        'py-2 px-4 flex items-center justify-between shrink-0', 
                        showBorderAfterTitle ? 'border-b border-slate-300'  : '', 
                        collapsable ? 'cursor-pointer' : '',
                        additionalHeaderClasses ?? '')}
                    onClick={() => collapsable && setCollapsed(prev => !prev)}>
                    {title === typeof 'string' ? 
                    <div className='text-sm font-semibold text-gray-800 uppercase'>
                        {title}
                    </div> : title}

                    <div className='flex items-center gap-2'>
                        {info && (
                            <>
                                <Tooltip id={tooltipId} place='bottom'>
                                    {info}
                                </Tooltip>

                                <span className='cursor-pointer' 
                                      data-tooltip-id={tooltipId} 
                                      onClick={(e) => e.stopPropagation()}>
                                    <FontAwesomeIcon icon={faCircleInfo} size='xs' />
                                </span>
                            </>
                        )}

                        {collapsable && (
                            <FontAwesomeIcon
                                icon={collapsed ? faChevronRight : faChevronDown}
                                size='xs'
                                className={`transition-transform duration-300 ${collapsed ? '-rotate-90' : ''}`}
                            />
                        )}
                    </div>
                </div>
            }

            {/* Content */}
            {collapsable ? (
                <div
                    className={classNames(
                        'overflow-hidden min-h-0',
                        hasMounted ? 'transition-[max-height,opacity] duration-300 ease-in-out' : '',
                    )}
                    style={{
                        maxHeight: collapsed ? '0px' : `${contentHeight}px`,
                        opacity: collapsed ? 0 : 1,
                    }}>
                    <div ref={contentRef} className={additionalContentClasses}>
                        {children}
                    </div>
                </div>
            ) : (
                <>
                    {additionalContentClasses != null ? (
                        <div className={additionalContentClasses}>
                            {children}
                        </div>
                    ) : (
                        <>{children}</>
                    )}
                </>
            )}
        </div>
    );
};

export default Card;